/**
 * CreateTemplateWizard - Main wizard component for creating templates
 * Converted from TypeScript for PromptusAI PWA
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StepIndicator } from './StepIndicator';
import { Step1ModelPrompt, Step2GalleryGeneration, Step3Details, Step4Preview } from './WizardSteps';
import { DefaultButtonSpinner, Icons, getOutputTypeFromCategory, detectMediaType } from './MediaComponents';

// Default values
const DEFAULT_CATEGORIES = [
  '3D',
  'API',
  'Anime',
  'Audio',
  'Audio Editing',
  'Audio to Audio',
  'ControlNet',
  'FLF2V',
  'Image',
  'Image Edit',
  'Image to 3D',
  'Image to Image',
  'Image to Video',
  'Inpainting',
  'LLM',
  'Outpainting',
  'Text to Audio',
  'Text to Image',
  'Text to Model',
  'Text to Video',
  'Upscale',
  'Vector',
  'Video',
  'Video to Video',
];

const DEFAULT_STEPS = [
  { number: 1, title: 'Model & Prompt', description: 'Choose model and base prompt' },
  { number: 2, title: 'Gallery Content', description: 'Generate content with optional inputs' },
  { number: 3, title: 'Details', description: 'Add title and description' },
  { number: 4, title: 'Preview', description: 'Review and submit' },
];

let wizardDraftCache = null;

const INITIAL_WIZARD_DATA = {
  model: '',
  basePrompt: '',
  galleryPrompt: '',
  generatedItems: [],
  selectedItemIds: [],
  uploadedImages: {},
  imagePreviews: {},
  fieldValues: {},
  title: '',
  description: '',
  category: '',
};

// Default alert
const defaultShowAlert = async (config) => {
  const confirmed = window.confirm(`${config.title}\n\n${config.text}`);
  return { isConfirmed: confirmed };
};

export function CreateTemplateWizard({
  isOpen,
  onClose,
  preSelectedCosyflow,
  onClearPreSelected,
  isLoggedIn,
  accountNo,
  username, // Username/alias for template name prefix
  fetchModels,
  fetchModelDefaults,
  fetchCosyflow,
  generateContent,
  createTemplate,
  onSuccess,
  onNavigateToTemplate,
  showAlert = defaultShowAlert,
  categories = DEFAULT_CATEGORIES,
  allowNewCategories = true,
  title = 'Create New Template',
  stepLabels,
  classNames = {},
  styles = {},
  LoadingSpinner,
  ButtonSpinner,
}) {
  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState(INITIAL_WIZARD_DATA);
  const [errors, setErrors] = useState({});

  // Model state
  const [models, setModels] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingModel, setIsLoadingModel] = useState(false);
  const [modelDefaults, setModelDefaults] = useState(null);
  const [selectedCosyflow, setSelectedCosyflow] = useState(null);
  const [imageFieldNames, setImageFieldNames] = useState([]);
  const [modelOutputType, setModelOutputType] = useState('image');

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isImageUploadExpanded, setIsImageUploadExpanded] = useState(false);

  // Refs
  const preSelectedLoadedRef = useRef(false);

  // Computed
  const canProceedToStep2 = wizardData.model.trim() !== '';
  const canProceedToStep3 = wizardData.selectedItemIds.length >= 1;
  const selectedItems = wizardData.generatedItems.filter(item => wizardData.selectedItemIds.includes(item.id));

  const resolvedStepLabels = stepLabels ? [
    stepLabels.step1 || DEFAULT_STEPS[0].title,
    stepLabels.step2 || DEFAULT_STEPS[1].title,
    stepLabels.step3 || DEFAULT_STEPS[2].title,
    stepLabels.step4 || DEFAULT_STEPS[3].title,
  ] : DEFAULT_STEPS.map(s => s.title);

  const SpinnerComponent = ButtonSpinner || <DefaultButtonSpinner />;

  const loadModelDefaults = useCallback(async (modelName) => {
    if (!modelName.trim()) return;
    setIsLoadingModel(true);
    try {
      const defaults = await fetchModelDefaults(modelName);
      setModelDefaults(defaults);
      if (defaults?.imageFieldNames && defaults.imageFieldNames.length > 0) {
        setImageFieldNames(defaults.imageFieldNames);
      } else {
        setImageFieldNames([]);
      }

      const cosyflowDef = await fetchCosyflow(modelName);
      if (cosyflowDef) {
        setSelectedCosyflow(cosyflowDef);
        const outputType = getOutputTypeFromCategory(cosyflowDef.category);
        setModelOutputType(outputType);
      } else {
        setModelOutputType('image');
      }
    } catch (error) {
      console.error('Error loading model defaults:', error);
      setImageFieldNames([]);
    } finally {
      setIsLoadingModel(false);
    }
  }, [fetchModelDefaults, fetchCosyflow]);

  // Effects
  useEffect(() => {
    if (isOpen && models.length === 0) {
      setIsLoadingModels(true);
      fetchModels()
        .then(setModels)
        .catch(console.error)
        .finally(() => setIsLoadingModels(false));
    }
  }, [isOpen, models.length, fetchModels]);

  useEffect(() => {
    if (!isOpen || !wizardDraftCache) return;
    const cacheKey = wizardDraftCache.key || null;
    const currentKey = preSelectedCosyflow?.uid || preSelectedCosyflow?.title || null;
    if (cacheKey && currentKey && cacheKey !== currentKey) return;
    setWizardData(wizardDraftCache.data);
    setCurrentStep(wizardDraftCache.step || 1);
    if (wizardDraftCache.model) {
      loadModelDefaults(wizardDraftCache.model);
    }
    preSelectedLoadedRef.current = true;
  }, [isOpen, preSelectedCosyflow, loadModelDefaults]);

  useEffect(() => {
    if (isOpen && preSelectedCosyflow && !preSelectedLoadedRef.current) {
      preSelectedLoadedRef.current = true;
      // Get username prefix from preSelectedCosyflow or username prop
      const usernamePrefix = preSelectedCosyflow.usernamePrefix || username || '';
      const titlePrefix = usernamePrefix ? `${usernamePrefix}/` : '';
      setWizardData(prev => ({
        ...prev,
        model: preSelectedCosyflow.title,
        title: titlePrefix, // Initialize title with username prefix
      }));
      loadModelDefaults(preSelectedCosyflow.title);
    }
    if (!isOpen) {
      preSelectedLoadedRef.current = false;
    }
  }, [isOpen, preSelectedCosyflow, loadModelDefaults, username]);

  useEffect(() => {
    if (!isOpen) return;
    wizardDraftCache = {
      key: preSelectedCosyflow?.uid || preSelectedCosyflow?.title || null,
      model: wizardData.model,
      step: currentStep,
      data: wizardData,
    };
  }, [isOpen, preSelectedCosyflow, wizardData, currentStep]);

  // Handlers
  const resetWizard = useCallback(() => {
    setCurrentStep(1);
    setWizardData(INITIAL_WIZARD_DATA);
    setModelDefaults(null);
    setErrors({});
    setIsAddingNewCategory(false);
    setNewCategoryName('');
    setImageFieldNames([]);
    setIsImageUploadExpanded(false);
    setSelectedCosyflow(null);
    setModelOutputType('image');
    preSelectedLoadedRef.current = false;
    onClearPreSelected?.();
    wizardDraftCache = null;
  }, [onClearPreSelected]);

  const handleClose = useCallback(() => {
    if (!isGenerating && !isSubmitting) {
      resetWizard();
      onClose();
    }
  }, [isGenerating, isSubmitting, resetWizard, onClose]);

  const clearSubsequentSteps = useCallback(() => {
    setWizardData(prev => ({
      ...prev,
      galleryPrompt: '',
      generatedItems: [],
      selectedItemIds: [],
      uploadedImages: {},
      imagePreviews: {},
      title: '',
      description: '',
      category: '',
    }));
    setIsImageUploadExpanded(false);
  }, []);

  const handleGenerateContent = async () => {
    console.log('[handleGenerateContent] CALLED - wizardData:', wizardData);

    const hasImportance5Variables = selectedCosyflow?.variables &&
      Object.values(selectedCosyflow.variables).some(v => v?.importance === 5);

    const hasPromptField = selectedCosyflow?.variables &&
      Object.entries(selectedCosyflow.variables).some(([key, v]) =>
        v?.importance === 5 && (key === 'prompt' || v?.type === 'prompt')
      );

    const prompt = wizardData.fieldValues?.prompt || wizardData.galleryPrompt;

    console.log('[handleGenerateContent] prompt:', prompt, 'fieldValues:', wizardData.fieldValues,
      'hasImportance5Variables:', hasImportance5Variables, 'hasPromptField:', hasPromptField);

    if (!hasImportance5Variables || hasPromptField) {
      if (!prompt?.trim()) {
        console.log('[handleGenerateContent] RETURNING - prompt is empty');
        setErrors({ galleryPrompt: 'Prompt is required' });
        return;
      }
    }

    console.log('[handleGenerateContent] isLoggedIn:', isLoggedIn);
    if (!isLoggedIn) {
      console.log('[handleGenerateContent] RETURNING - not logged in');
      await showAlert({ title: 'Login Required', text: 'You need to be logged in to generate content.', icon: 'warning' });
      return;
    }

    console.log('[handleGenerateContent] Starting generation...');
    setIsGenerating(true);
    setGenerationProgress(0);
    setErrors({});

    try {
      const result = await generateContent({
        prompt,
        basePrompt: wizardData.basePrompt,
        model: wizardData.model,
        modelDefaults,
        imageInputs: wizardData.imagePreviews,
        fieldValues: wizardData.fieldValues,
        onProgress: setGenerationProgress,
      });

      if (result) {
        const detectedFromContent = detectMediaType(result.image);
        const actualMediaType = detectedFromContent !== 'image' ? detectedFromContent : (result.type || modelOutputType);

        const newItem = {
          id: `gallery-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          prompt,
          imageUrl: result.image,
          serverUrl: result.serverUrl,
          mediaType: actualMediaType,
        };

        setWizardData(prev => ({
          ...prev,
          generatedItems: [...prev.generatedItems, newItem],
        }));
      }
    } catch (error) {
      console.error('Generation error:', error);
      await showAlert({ title: 'Generation Failed', text: error.message || 'Failed to generate content.', icon: 'error' });
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const validateStep3 = () => {
    const newErrors = {};
    // Check if title has content beyond just the username prefix
    const usernamePrefix = preSelectedCosyflow?.usernamePrefix || username || '';
    const titlePrefix = usernamePrefix ? `${usernamePrefix}/` : '';
    const titleWithoutPrefix = wizardData.title.startsWith(titlePrefix)
      ? wizardData.title.slice(titlePrefix.length)
      : wizardData.title;
    if (!titleWithoutPrefix.trim()) newErrors.title = 'Title is required';
    if (!wizardData.description.trim()) newErrors.description = 'Description is required';
    if (!wizardData.category.trim()) newErrors.category = 'Use case is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isLoggedIn || !accountNo) {
      await showAlert({ title: 'Login Required', text: 'You need to be logged in to create a template.', icon: 'warning' });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createTemplate({
        formData: {
          title: wizardData.title,
          description: wizardData.description,
          category: wizardData.category,
          model: wizardData.model,
          basePrompt: wizardData.basePrompt,
          galleryItems: selectedItems,
        },
        cosyflowDefinition: selectedCosyflow,
        accountNo,
      });

      if (result.error) throw new Error(result.error);

      const templateId = result.id;

      const alertResult = await showAlert({
        title: 'Template Created!',
        text: 'Your template has been created successfully.',
        icon: 'success',
        confirmButtonText: 'Go to Template',
        showCancelButton: true,
        cancelButtonText: 'Close',
      });

      if (templateId) {
        onSuccess?.(templateId);
        if (alertResult.isConfirmed) {
          onNavigateToTemplate?.(templateId);
        }
      }

      resetWizard();
      onClose();
    } catch (error) {
      console.error('Error creating template:', error);
      await showAlert({ title: 'Error', text: error.message || 'Failed to create template. Please try again.', icon: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToNextStep = async () => {
    if (currentStep === 1 && !canProceedToStep2) {
      await showAlert({ title: 'Model Required', text: 'Please select a model before proceeding.', icon: 'info' });
      return;
    }
    if (currentStep === 2 && !canProceedToStep3) {
      await showAlert({ title: 'Select Content', text: 'Please select at least one item for your gallery.', icon: 'info' });
      return;
    }
    if (currentStep === 3 && !validateStep3()) return;
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const goToPrevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm ${classNames.backdrop || ''}`}
        style={styles.backdrop}
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`bg-dark-blue border-2 border-cyan/50 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${classNames.modal || ''}`}
          style={styles.modal}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`bg-dark-blue/80 border-b border-white/10 p-6 flex items-center justify-between ${classNames.header || ''}`} style={styles.header}>
            <div>
              <h2 className={`text-xl font-bold text-white ${classNames.headerTitle || ''}`}>{title}</h2>
              <p className="text-sm text-white/60 mt-1">Step {currentStep} of 4: {DEFAULT_STEPS[currentStep - 1].description}</p>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/60 hover:text-white" disabled={isGenerating || isSubmitting}>
              <Icons.Close />
            </button>
          </div>

          {/* Step Indicator */}
          <div className={`px-6 py-4 bg-dark-blue/50 border-b border-white/10 ${classNames.stepIndicator || ''}`}>
            <StepIndicator currentStep={currentStep} totalSteps={4} stepLabels={resolvedStepLabels} />
          </div>

          {/* Content */}
          <div className={`flex-1 overflow-y-auto p-6 ${classNames.content || ''}`} style={styles.content}>
            {currentStep === 1 && (
              <Step1ModelPrompt
                wizardData={wizardData} setWizardData={setWizardData}
                errors={errors} setErrors={setErrors}
                models={models} isLoadingModels={isLoadingModels} isLoadingModel={isLoadingModel}
                isGenerating={isGenerating} onModelChange={loadModelDefaults}
                onClearSubsequentSteps={clearSubsequentSteps} ButtonSpinner={SpinnerComponent}
              />
            )}
            {currentStep === 2 && (
              <Step2GalleryGeneration
                wizardData={wizardData} setWizardData={setWizardData}
                errors={errors} setErrors={setErrors}
                imageFieldNames={imageFieldNames}
                isImageUploadExpanded={isImageUploadExpanded} setIsImageUploadExpanded={setIsImageUploadExpanded}
                isGenerating={isGenerating} generationProgress={generationProgress}
                modelOutputType={modelOutputType} onGenerateContent={handleGenerateContent}
                ButtonSpinner={SpinnerComponent}
                selectedCosyflow={selectedCosyflow}
              />
            )}
            {currentStep === 3 && (
              <Step3Details
                wizardData={wizardData} setWizardData={setWizardData}
                errors={errors} setErrors={setErrors}
                categories={categories} allowNewCategories={allowNewCategories}
                isAddingNewCategory={isAddingNewCategory} setIsAddingNewCategory={setIsAddingNewCategory}
                newCategoryName={newCategoryName} setNewCategoryName={setNewCategoryName}
                isSubmitting={isSubmitting}
                usernamePrefix={preSelectedCosyflow?.usernamePrefix || username || ''}
              />
            )}
            {currentStep === 4 && (
              <Step4Preview
                wizardData={wizardData} setWizardData={setWizardData}
                errors={errors} setErrors={setErrors}
                selectedItems={selectedItems} modelOutputType={modelOutputType}
              />
            )}
          </div>

          {/* Footer */}
          <div className={`bg-dark-blue/80 border-t border-white/10 p-6 flex gap-3 ${classNames.footer || ''}`} style={styles.footer}>
            {currentStep > 1 && (
              <button
                onClick={goToPrevStep}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium bg-dark-blue/50 border border-white/20 text-white hover:bg-dark-blue/70 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                disabled={isGenerating || isSubmitting}
              >
                <Icons.ChevronLeft /> Back
              </button>
            )}

            {currentStep < 4 ? (
              <button
                onClick={goToNextStep}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium bg-yellow text-dark-blue hover:bg-yellow/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                disabled={isGenerating}
              >
                Next <Icons.ChevronRight />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium bg-yellow text-dark-blue hover:bg-yellow/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                disabled={isSubmitting}
              >
                {isSubmitting ? (<>{SpinnerComponent} Creating...</>) : (<><Icons.Plus /> Create Template</>)}
              </button>
            )}

            {currentStep === 1 && (
              <button onClick={handleClose} className="px-4 py-2.5 rounded-lg font-medium bg-dark-blue/50 border border-white/20 text-white hover:bg-dark-blue/70 transition-colors" disabled={isGenerating}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default CreateTemplateWizard;
